/**
 * Normalize CasaMapa neighborhood labels (accents, casing, common prefixes).
 */

const SMALL_WORDS = new Set(["de", "del", "la", "las", "los", "y", "e", "el"]);

function stripAccents(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

/**
 * Accent-insensitive key used to merge spelling variants.
 */
export function casaMapaNeighborhoodKey(value) {
  return stripAccents(value)
    .toLowerCase()
    .replace(/^(barrio|barrios|sector|zona)\s+/i, "")
    .replace(/[^a-z0-9]+/g, "")
    .trim();
}

/** Well-known Medellín-metro barrio spellings. */
const ALIAS_TO_CANONICAL = Object.freeze({
  belen: "Belén",
  elpoblado: "El Poblado",
  poblado: "El Poblado",
  laureles: "Laureles",
  estadio: "Estadio",
  lomaolosbernal: "Loma de los Bernal",
  lomadelosbernal: "Loma de los Bernal",
  castellana: "La Castellana",
  lacastellana: "La Castellana",
  america: "América",
  florida: "La Florida",
  laflorida: "La Florida",
});

function accentScore(value) {
  const nfd = String(value || "").normalize("NFD");
  return (nfd.match(/[\u0300-\u036f]/g) || []).length;
}

function titleCaseNeighborhood(value) {
  const parts = String(value || "")
    .trim()
    .replace(/\s+/g, " ")
    .split(" ");

  return parts
    .map((part, index) => {
      if (!part) return part;
      const lower = part.toLocaleLowerCase("es-CO");
      if (index > 0 && SMALL_WORDS.has(stripAccents(lower))) {
        return lower;
      }
      return lower.charAt(0).toLocaleUpperCase("es-CO") + lower.slice(1);
    })
    .join(" ");
}

/**
 * Map a raw neighborhood string to a display-normalized label.
 */
export function normalizeCasaMapaNeighborhood(value) {
  const raw = String(value || "")
    .trim()
    .replace(/\s+/g, " ")
    .replace(/^(barrio|barrios|sector|zona)\s+/i, "")
    .trim();
  if (!raw) {
    return null;
  }
  const key = casaMapaNeighborhoodKey(raw);
  if (!key) {
    return null;
  }
  if (ALIAS_TO_CANONICAL[key]) {
    return ALIAS_TO_CANONICAL[key];
  }
  return titleCaseNeighborhood(raw);
}

function preferNeighborhoodLabel(current, candidate) {
  if (!current) {
    return candidate;
  }
  if (!candidate) {
    return current;
  }
  const currentScore = accentScore(current);
  const candidateScore = accentScore(candidate);
  if (candidateScore > currentScore) {
    return candidate;
  }
  if (candidateScore < currentScore) {
    return current;
  }
  // Prefer the version that keeps a leading article when both are otherwise equal.
  const currentHasArticle = /^(el|la|los|las)\s+/i.test(current);
  const candidateHasArticle = /^(el|la|los|las)\s+/i.test(candidate);
  if (candidateHasArticle && !currentHasArticle) {
    return candidate;
  }
  return current;
}

/**
 * Merge facet rows that are spelling variants of the same neighborhood.
 * Counts are summed; the best accented/title-cased label is kept.
 */
export function mergeCasaMapaNeighborhoodFacetOptions(facetRows = []) {
  const rows = Array.isArray(facetRows) ? facetRows : [];
  const byKey = new Map();

  rows.forEach((row) => {
    const raw = row?.value ?? row?.label;
    const canonical = normalizeCasaMapaNeighborhood(raw);
    if (!canonical) {
      return;
    }
    const key = casaMapaNeighborhoodKey(canonical);
    const existing = byKey.get(key);
    const count = Number(row?.count);
    const safeCount = Number.isFinite(count) ? count : 0;

    if (existing) {
      existing.count =
        (existing.count != null ? existing.count : 0) + safeCount;
      existing.label = preferNeighborhoodLabel(existing.label, canonical);
      existing.value = existing.label;
      return;
    }

    byKey.set(key, {
      value: canonical,
      label: canonical,
      count: Number.isFinite(count) ? count : null,
    });
  });

  return [...byKey.values()].sort((a, b) => {
    const countDelta = (b.count || 0) - (a.count || 0);
    if (countDelta !== 0) {
      return countDelta;
    }
    return String(a.label).localeCompare(String(b.label), "es");
  });
}

/**
 * Accent-insensitive substring match for typeahead filtering.
 */
export function neighborhoodMatchesQuery(label, query) {
  const q = casaMapaNeighborhoodKey(query);
  if (!q) {
    return true;
  }
  return casaMapaNeighborhoodKey(label).includes(q);
}
